import { ComponentType, Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal, TemplatePortal } from "@angular/cdk/portal";
import {
	DestroyRef,
	Injectable,
	InjectionToken,
	Injector,
	TemplateRef,
	ViewContainerRef,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { DialogConfig } from "./dialog-config";
import { DialogContainerComponent } from "./dialog-container.component";
import { DialogRef } from "./dialog-ref";

export const DIALOG_DATA = new InjectionToken<any>("DialogData");

@Injectable()
export class DialogService {
	constructor(
		private overlay: Overlay,
		private injector: Injector,
		private destroyRef: DestroyRef,
	) {}

	open<T, D = any, R = any>(
		componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
		config?: DialogConfig<D>,
	): DialogRef<T> {
		config = this.applyConfigDefaults(config, new DialogConfig());
		const overlayRef = this.createOverlay(config);
		const dialogContainer = this.attachDialogContainer(overlayRef, config);

		return this.attachDialogContent<T, R>(
			componentOrTemplateRef,
			dialogContainer,
			overlayRef,
			config,
		);
	}

	private createOverlay(config: DialogConfig): OverlayRef {
		const overlayConfig = this.getOverlayConfig(config);
		return this.overlay.create(overlayConfig);
	}

	private getOverlayConfig(config: DialogConfig): OverlayConfig {
		const state = new OverlayConfig({
			positionStrategy: this.overlay.position().global().top("0").right("0"),
			panelClass: config.panelClass,
			hasBackdrop: config.hasBackdrop,
			minWidth: config.minWidth,
			minHeight: config.minHeight,
			maxWidth: config.maxWidth,
			maxHeight: config.maxHeight,
			disposeOnNavigation: true,
			backdropClass:
				config.backdrop === "transparent"
					? "cdk-overlay-transparent-backdrop"
					: "cdk-overlay-dark-backdrop",
			scrollStrategy:
				config.scrollStrategy === "block"
					? this.overlay.scrollStrategies.block()
					: this.overlay.scrollStrategies.noop(),
		});

		if (config.backdropClass) {
			state.backdropClass = config.backdropClass;
		}

		return state;
	}

	private applyConfigDefaults(
		config?: DialogConfig,
		defaultOptions?: DialogConfig,
	): DialogConfig {
		return { ...defaultOptions, ...config };
	}

	private attachDialogContainer(
		overlay: OverlayRef,
		config: DialogConfig,
	): DialogContainerComponent {
		const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
		const options = {
			providers: [{ provide: DialogConfig, useValue: config }],
			parent: userInjector || this.injector,
		};
		const injector = Injector.create(options);
		const containerPortal = new ComponentPortal(
			DialogContainerComponent,
			config.viewContainerRef,
			injector,
		);
		const containerRef = overlay.attach<DialogContainerComponent>(containerPortal);

		return containerRef.instance;
	}

	private attachDialogContent<T, R>(
		componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
		dialogContainer: DialogContainerComponent,
		overlayRef: OverlayRef,
		config: DialogConfig,
	): DialogRef<T, R> {
		const dialogRef = new DialogRef<T, R>(overlayRef, config.id, dialogContainer);

		if (config.hasBackdrop && !config.disableClose) {
			overlayRef
				.backdropClick()
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					dialogRef.close();
				});
		}

		if (componentOrTemplateRef instanceof TemplateRef) {
			dialogContainer.attachTemplatePortal(
				new TemplatePortal<T>(
					componentOrTemplateRef,
					{} as ViewContainerRef,
					{ $implicit: config.data, dialogRef } as any,
				),
			);
		} else {
			const injector = this.createInjector<T>(config, dialogRef, dialogContainer);
			const contentRef = dialogContainer.attachComponentPortal<T>(
				new ComponentPortal(componentOrTemplateRef, undefined, injector),
			);
			dialogRef.componentInstance = contentRef.instance;
		}

		dialogRef.updateSize(config.width, config.height).updatePosition(config.position);

		return dialogRef;
	}

	private createInjector<T>(
		config: DialogConfig,
		dialogRef: DialogRef<T>,
		dialogContainer: DialogContainerComponent,
	): Injector {
		const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

		const options = {
			providers: [
				{ provide: DialogContainerComponent, useValue: dialogContainer },
				{ provide: DIALOG_DATA, useValue: config.data },
				{ provide: DialogRef, useValue: dialogRef },
			],
			parent: userInjector || this.injector,
		};

		return Injector.create(options);
	}
}
